<script setup lang="ts">
//#region import
import { h, nextTick, ref, resolveComponent } from 'vue';
import { isEmpty, merge, omit } from 'lodash-es';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { useSessionAction } from '@/composables/useSessionAction';
import { usePopupManager } from '@/components/PopupManager.vue';
import updateUrl from '../../service/updateUrl';
import type { GetControlTowerMediaBuyingPaymentMethodsData, GetControlTowerMediaBuyingPaymentMethodsResponse } from '@/types/openapi-ts';
import type { SettingsPageQuery } from '@/types/core';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
//#endregion

//#region types
type MediaBuyingQuery = SettingsPageQuery<GetControlTowerMediaBuyingPaymentMethodsData['query']>;
//#endregion

//#region const
const sizes = {
  1080: 20,
  1440: 30,
};
const LS_PATH = 'MediaBuyingSettings';
const fields: UiTableNewField<GetControlTowerMediaBuyingPaymentMethodsResponse['payload'][number]>[] = [
  {
    name: 'id',
    width: '250',
    align: 'left',
  },
  {
    name: 'status',
    minWidth: '80',
    type: 'status',
    statusText: (value, row) => t(`settings.paymentMethods.statuses.${row?.hidden ? 'disabled' : 'active'}`),
    status: (value, row) => (row?.hidden ? 'negative' : 'positive'),
  },
  {
    name: 'name',
    minWidth: '264',
    sortable: 'custom',
  },
  {
    name: 'currencies',
    minWidth: '264',
    linkAppend: (row) => renderCurrencies(row, 10),
  },
  {
    name: 'logo',
    minWidth: '120',
    align: 'center',
    headerAlign: 'center',
    computedImg: (row) => row.logo_base64!,
    computedImgNull: () => '',
  },
  {
    name: 'actions',
    width: '80',
    align: 'center',
    headerAlign: 'center',
    actionLib: 'fa',
    controlType: 'icon-action',
    controlColorIco: () => 'var(--text-primary)',
    action: (row) => handleOpenPaymentMethod(row),
    actionName: 'pen',
    tooltip: 'crm.buttons.edit',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const { t } = useI18n();
const { onSessionUnlock } = useSessionAction();
const popupManager = usePopupManager();
//#endregion

//#region data

const tableData = ref<GetControlTowerMediaBuyingPaymentMethodsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
// const options = ref({
//   sort_column: '',
//   sort_dir: '',
// });

const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort } = useTableSort({
  lsPath: LS_PATH,
  fields,
});

const { searchQuery, setSearch } = useTableSearch();
//#endregion

//#region methods
const _query = () => {
  return {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
  } as GetControlTowerMediaBuyingPaymentMethodsData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters: MediaBuyingQuery = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  updateUrl.updateGetParams(_dataFilters);
};

const fetchData = () => {
  if(!isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  const options = _query();

  api.getControlMediaBuyingPaymentMethods(options).then((res) => {
    tableData.value = res.data.payload;
    count.value = res.data.misc!.count;
  }).finally(() => {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  });
};

const initData = () => {
  const _urlData: MediaBuyingQuery = updateUrl.getParseParams(route.hash);
  if (!isEmpty(_urlData)) {
    setPage(_urlData);

    delete _urlData.page;
    delete _urlData.limit;
    merge(optionsSort.value, _urlData);

    setSort(_urlData);
    setSearch(_urlData);
  }
  nextTick(() => {
    fetchData();
  });
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleOpenPaymentMethod = (row?: GetControlTowerMediaBuyingPaymentMethodsResponse['payload'][number]) => {
  popupManager.addPaymentMethodMediaBuying.open({
    data: {
      id: row?.id,
    },
    listeners: {
      success: () => {
        fetchData();
      },
    },
  });
};

function renderCurrencies(item: GetControlTowerMediaBuyingPaymentMethodsResponse['payload'][number], count: number) {
  if (item.allowed_currencies === 'all') {
    return h('div', {class: 't:truncate'}, t('settings.paymentMethods.popup.all'));
  }
  const visibleCurrencies = item.currencies.slice(0, count).join(', ');

  if (item.currencies.length <= count) {
    return h('div', {class: 't:truncate'}, visibleCurrencies);
  }

  const Button = resolveComponent('ui-button');
  const UiDropdown = resolveComponent('ui-dropdown');
  const otherCurrencies = item.currencies.slice(count).map((currency) => currency);

  const dropdown = h(
    UiDropdown,
    {
      itemsList: otherCurrencies,
    },
    {
      default: () => h(Button,  {size: 'small', class: 't:ml-4'}, {
        default: () => `+${otherCurrencies.length}`,
      }),
    },
  );

  return h('div', { class: 't:flex t:items-center t:w-full' }, [visibleCurrencies, dropdown]);
}
//#endregion


//#region hooks
onSessionUnlock.value = initData;
initData();
//#endregion
</script>

<template>
  <div class="media-buying-settings">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('settings.mediaBuyingPaymentMethods.title') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          filled
          icon="plus"
          @click="handleOpenPaymentMethod()"
        >
          {{ $t('settings.paymentMethods.addPaymentMethod') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="settings.mediaBuyingPaymentMethods"
      @sort="handleSort"
    />
  </div>
</template>
